import { Plus, UserPlus, Users, X } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { EndpointPicker, type EndpointRef } from "@/features/endpoint-picker";
import { ApiError } from "@/lib/api";
import { repNumberRule, repNumbers, unroutedNumbers } from "@/lib/campaign";
import { linePurposeText } from "@/lib/line";
import {
  useAddLineMember, useCampaignMembers, useDids, useRemoveLineMember, useUpdateCampaign,
  useUpdateDid,
} from "@/lib/queries";
import type { Campaign, Did, QueueMember } from "@/lib/types";
import { formatPhoneNumber } from "@/lib/utils";
import { CampaignTeams, TeamFromLineDialog } from "@/features/campaign-teams";
import { addedByHand } from "@/lib/teams";

/**
 * Who takes the campaign's calls, and how: the reps on each of its lines, and whether their phones
 * answer the campaign's calls by themselves.
 *
 * A membership is a phone, not a person — the ACD rings a SIP account — so a rep is added by
 * picking their phone, found by their name. Staffing is per line, and taking a rep off one line
 * leaves their others alone. Most reps are on a line through a team (platform ADR-185,
 * `campaign-teams.tsx`). This table lists everyone, each with how they are there: a rep somebody
 * added by hand can be taken off here, which leaves them on a line a team still puts them on; a
 * rep only a team puts there comes off through the team. Being added rings nobody; the rep is
 * offered the line's calls once they are Ready.
 *
 * Each rep's own number on the campaign shows beside them (ADR-037), and an admin can give any rep
 * on the primary line, by hand or through a team, one of the tenant's unrouted numbers, and take
 * one back again, without going to Numbers. A number is the rep's, never the team's (platform
 * ADR-195).
 */
export function CampaignStaffing({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const { data: everyone, isPending, error, refetch } = useCampaignMembers(campaign.id);
  const members = everyone ?? [];
  // What "Make a team from reps added by hand" takes.
  const byHand = addedByHand(members);
  const teamed = everyone !== undefined && everyone.some((m) => m.by_hand !== undefined);
  const [makingTeam, setMakingTeam] = React.useState(false);
  const remove = useRemoveLineMember();
  const [adding, setAdding] = React.useState(false);
  const [removing, setRemoving] = React.useState<QueueMember | null>(null);
  const [giving, setGiving] = React.useState<QueueMember | null>(null);
  const [taking, setTaking] = React.useState<Did | null>(null);
  const purposeOf = new Map(campaign.lines.map((line) => [line.id, line.purpose]));
  const { data: dids } = useDids({ campaign_id: campaign.id });
  const numbersOf = repNumbers(dids ?? [], campaign.id);

  const addButton = canManage && (
    <span className="inline-flex gap-2">
      {teamed && byHand.length > 0 && (
        <Button variant="outline" size="sm" onClick={() => setMakingTeam(true)}>
          <Users />
          Make a team from reps added by hand
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={() => setAdding(true)} disabled={campaign.lines.length === 0}>
        <UserPlus />
        Add a rep
      </Button>
    </span>
  );

  return (
    <div className="space-y-4">
      <AutoAnswer campaign={campaign} canManage={canManage} />
      <OwnCallsOnly campaign={campaign} canManage={canManage} />
      <CampaignTeams campaign={campaign} canManage={canManage} />

      <Card className="overflow-hidden">
        <CardHeader
          title={teamed ? "Reps" : "Staffing"}
          description={
            teamed
              ? "Everyone on the campaign's lines, added by hand or through a team, with their own numbers. A rep a team puts on a line comes off it through the team. A rep is offered a line's calls while they are Ready."
              : "The reps on each of the campaign's lines. A rep is offered a line's calls while they are Ready."
          }
          action={addButton}
        />

        {isPending ? (
          <TableSkeleton cols={4} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load the staffing"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : members.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nobody staffs this campaign yet"
            description="Add the reps who take its calls, or put a team on a line. Until then, a call to it waits with nobody to ring."
            action={addButton || undefined}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rep</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Line</TableHead>
                <TableHead>Skills</TableHead>
                <TableHead>Number</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => {
                const skills = Object.entries(m.skills ?? {});
                const teams = (m.teams ?? []).join(", ");
                return (
                  <TableRow key={`${m.queue_id}:${m.user}@${m.domain}`}>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-sm text-text">{m.display_name ?? m.user}</span>
                        {!m.enabled && (
                          <Badge tone="outline" size="sm">
                            Disabled
                          </Badge>
                        )}
                        {m.sip_endpoint_id === null && (
                          <Badge tone="warning" size="sm">
                            Phone gone
                          </Badge>
                        )}
                        {/* Only a team puts them here: they come off through the team. */}
                        {m.by_hand === false && teams && (
                          <Badge tone="outline" size="sm" title="On this line through the team. They come off it through the team.">
                            Through {teams}
                          </Badge>
                        )}
                        {/* On the line through a team as well: taken off here, the team keeps them. */}
                        {m.by_hand !== false && teams && (
                          <Badge tone="outline" size="sm" title="Taken off here, they stay on the line through the team">
                            Also in {teams}
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    <MonoCell>
                      {m.user}@{m.domain}
                    </MonoCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="font-mono text-xs">{m.queue_name}</span>
                        <Badge tone="neutral" size="sm">
                          {linePurposeText(purposeOf.get(m.queue_id) ?? null).label}
                        </Badge>
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-text-muted">
                      {skills.length === 0
                        ? "—"
                        : skills.map(([slug, level]) => `${slug} ${level}`).join(", ")}
                    </TableCell>
                    <TableCell>
                      <RepNumberCell
                        numbers={m.sip_endpoint_id === null ? [] : (numbersOf.get(m.sip_endpoint_id) ?? [])}
                        who={m.display_name ?? m.user}
                        canManage={canManage}
                        canGive={canManage && m.sip_endpoint_id !== null && m.queue_id === campaign.queue_id}
                        onGive={() => setGiving(m)}
                        onTake={setTaking}
                      />
                    </TableCell>
                    <TableCell>
                      {canManage && m.sip_endpoint_id !== null && m.by_hand !== false && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Take ${m.display_name ?? m.user} off ${m.queue_name}`}
                          onClick={() => setRemoving(m)}
                        >
                          <X />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <TeamFromLineDialog
          open={makingTeam}
          onOpenChange={setMakingTeam}
          campaign={campaign}
          members={everyone ?? []}
        />

        <AddRepDialog
          open={adding}
          onOpenChange={setAdding}
          campaign={campaign}
          members={everyone ?? []}
        />

        <GiveNumberDialog
          member={giving}
          onOpenChange={(open) => !open && setGiving(null)}
          campaign={campaign}
        />

        <TakeNumberDialog
          did={taking}
          onOpenChange={(open) => !open && setTaking(null)}
          campaign={campaign}
        />

        <ConfirmDialog
          open={removing !== null}
          onOpenChange={(open) => !open && setRemoving(null)}
          title="Take this rep off the line?"
          description={
            <>
              <span className="font-medium">{removing?.display_name ?? removing?.user}</span> comes
              off <span className="font-mono">{removing?.queue_name}</span>. Their other lines stay as
              they are.
            </>
          }
          confirmLabel="Take off the line"
          destructive
          loading={remove.isPending}
          onConfirm={async () => {
            if (removing?.sip_endpoint_id == null) return;
            try {
              await remove.mutateAsync({
                lineId: removing.queue_id,
                sipEndpointId: removing.sip_endpoint_id,
              });
              toast.success("Rep taken off the line", removing.queue_name);
              setRemoving(null);
            } catch (e) {
              toast.error("Could not take the rep off", e instanceof ApiError ? e.message : "Please try again.");
            }
          }}
        />
      </Card>
    </div>
  );
}

/**
 * Whether the campaign's reps answer its calls without a click (`force_auto_answer`).
 *
 * Beside who takes the calls, because it is about how they take them. It covers the calls the ACD
 * sends on the campaign's lines while a rep takes calls, whatever that rep's browser is set to —
 * which still decides their other calls — and it saves on its own, like the status in the page
 * header. The dialer is why it exists: a lead who answers is held for their rep for only the
 * campaign's abandon window, and a rep who has to reach for Answer misses most of them.
 */
function AutoAnswer({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const update = useUpdateCampaign();
  const on = campaign.force_auto_answer;

  const change = (next: boolean) =>
    update.mutate(
      { id: campaign.id, force_auto_answer: next },
      {
        onSuccess: () =>
          toast.success(
            next ? "Reps' phones answer this campaign's calls" : "Reps answer this campaign's calls themselves",
            campaign.name,
          ),
        onError: (e) =>
          toast.error(
            "Could not change auto-answer",
            e instanceof ApiError ? e.message : "Please try again.",
          ),
      },
    );

  return (
    <Card>
      <CardBody className="flex items-center justify-between gap-4">
        <div>
          <p id="campaign-auto-answer" className="text-sm font-medium text-text">
            Answer calls automatically
          </p>
          <p className="text-xs text-text-muted">
            {on
              ? "A rep's phone picks up this campaign's calls by itself while they take calls. Their other calls ring as usual."
              : "Reps click Answer. A lead the dialer reaches waits for only a moment, so a dialing campaign should answer automatically."}
          </p>
        </div>
        <Switch
          checked={on}
          onCheckedChange={change}
          disabled={!canManage || update.isPending}
          aria-labelledby="campaign-auto-answer"
        />
      </CardBody>
    </Card>
  );
}

/** Whether a rep's own number on the campaign keeps them to their own callers there (ADR-037). */
function OwnCallsOnly({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const update = useUpdateCampaign();
  const on = campaign.own_calls_only_allowed ?? true;

  const change = (next: boolean) =>
    update.mutate(
      { id: campaign.id, own_calls_only_allowed: next },
      {
        onSuccess: () =>
          toast.success(
            next ? "Reps with their own number take only their own callers" : "Reps take every call on this campaign",
            campaign.name,
          ),
        onError: (e) =>
          toast.error(
            "Could not change who reps with their own number take",
            e instanceof ApiError ? e.message : "Please try again.",
          ),
      },
    );

  return (
    <Card>
      <CardBody className="flex items-center justify-between gap-4">
        <div>
          <p id="campaign-own-calls-only" className="text-sm font-medium text-text">
            Reps with their own number take only their own callers
          </p>
          <p className="text-xs text-text-muted">
            {on
              ? "A rep with their own number on this campaign takes only their own callers here: callers who dial their number, and callers a colleague sends them by name. The line's other calls go to the other reps. Their phone still reads Ready."
              : "Every rep on this campaign's lines takes all of its calls, reps with their own number too."}
          </p>
        </div>
        <Switch
          checked={on}
          onCheckedChange={change}
          disabled={!canManage || update.isPending}
          aria-labelledby="campaign-own-calls-only"
        />
      </CardBody>
    </Card>
  );
}

/** A rep's own numbers on the campaign, each with a way to take it back, or a way to give one. */
function RepNumberCell({
  numbers, who, canManage, canGive, onGive, onTake,
}: {
  numbers: Did[];
  who: string;
  canManage: boolean;
  canGive: boolean;
  onGive: () => void;
  onTake: (did: Did) => void;
}) {
  if (numbers.length > 0) {
    return (
      <span className="flex flex-col items-start gap-1">
        {numbers.map((d) => (
          // The number and its X read as one chip, so the X beside it is plainly the number's
          // and not the row's, which takes the rep off the line altogether.
          <span
            key={d.id}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-border py-0.5 pl-1.5 pr-1 font-mono text-xs text-text"
          >
            {formatPhoneNumber(d.number)}
            {canManage && (
              <Button
                variant="ghost"
                size="icon-sm"
                className="size-4 rounded-sm [&_svg]:size-3"
                aria-label={`Take ${formatPhoneNumber(d.number)} back from ${who}`}
                onClick={() => onTake(d)}
              >
                <X />
              </Button>
            )}
          </span>
        ))}
      </span>
    );
  }
  if (!canGive) return <span className="text-xs text-text-muted">No number</span>;
  return (
    <Button variant="link" size="sm" className="h-auto px-0" onClick={onGive}>
      <Plus />
      Give a number
    </Button>
  );
}

/**
 * The number stops being this rep's. What it does instead is the choice, because both answers are
 * ordinary: a number the campaign still wants answered goes back to being the line's, and one
 * given out by mistake goes back to the unrouted numbers, ready for somebody else.
 *
 * Either way it is one `PUT /dids/{id}`, the same write the Numbers page saves — the rep is
 * dropped from the route, not from the line, so their staffing and their other numbers are
 * untouched.
 */
export function TakeNumberDialog({
  did, onOpenChange, campaign,
}: {
  /** The number's id and what the dialog says about it: the Numbers tab hands its own row. */
  did: Pick<Did, "id" | "number" | "sip_endpoint_user_name" | "sip_endpoint_extension"> | null;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}) {
  const toast = useToast();
  const update = useUpdateDid();
  const open = did !== null;
  const [keepOnLine, setKeepOnLine] = React.useState(true);
  const [formError, setFormError] = React.useState<string | null>(null);
  const who = did?.sip_endpoint_user_name ?? did?.sip_endpoint_extension ?? "the rep";

  React.useEffect(() => {
    if (!open) return;
    setKeepOnLine(true);
    setFormError(null);
  }, [open]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!did) return;
    setFormError(null);
    try {
      await update.mutateAsync(
        keepOnLine
          ? { id: did.id, action_type: "campaign", campaign_id: campaign.id, sip_endpoint_id: null }
          : { id: did.id, action_type: null, campaign_id: null, sip_endpoint_id: null },
      );
      toast.success(
        "Number taken back",
        keepOnLine
          ? `${formatPhoneNumber(did.number)} rings anyone on ${campaign.name}`
          : `${formatPhoneNumber(did.number)} is not routed`,
      );
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not take the number back.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Take {did ? formatPhoneNumber(did.number) : "the number"} back</DialogTitle>
          <DialogDescription>
            It stops ringing {who} first. They stay on the campaign.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <fieldset className="min-w-0 space-y-2">
              <legend className="mb-2 text-xs font-medium text-text">Who answers it now</legend>
              <div className="grid gap-2">
                <TakeChoice
                  checked={keepOnLine}
                  onSelect={() => setKeepOnLine(true)}
                  title="Anyone on the line"
                  description={`Callers still reach ${campaign.name}, and whoever is free takes them.`}
                />
                <TakeChoice
                  checked={!keepOnLine}
                  onSelect={() => setKeepOnLine(false)}
                  title="Nobody — stop using the number"
                  description="Callers hear that it cannot be reached. It goes back to the numbers you can give out."
                />
              </div>
            </fieldset>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={update.isPending}>
              Take it back
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function TakeChoice({
  checked, onSelect, title, description,
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2">
      <input
        type="radio"
        name="take-number-who"
        className="mt-0.5 accent-accent-9"
        checked={checked}
        onChange={onSelect}
      />
      <span className="min-w-0">
        <span className="block text-xs font-medium text-text">{title}</span>
        <span className="block text-xs text-text-muted">{description}</span>
      </span>
    </label>
  );
}

/**
 * One of the tenant's unrouted numbers, pointed at this rep on the campaign: the same route the
 * Numbers page saves, a campaign route with the rep named (ADR-037).
 */
export function GiveNumberDialog({
  member, onOpenChange, campaign,
}: {
  member: QueueMember | null;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}) {
  const toast = useToast();
  const update = useUpdateDid();
  const open = member !== null;
  const { data: dids, isPending } = useDids(
    { tenant_id: campaign.tenant_id, action_type: "none" },
    { enabled: open },
  );
  const [didId, setDidId] = React.useState<number | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);
  const choices = unroutedNumbers(dids ?? [], campaign.tenant_id);
  const name = member?.display_name ?? member?.user ?? "the rep";

  React.useEffect(() => {
    if (!open) return;
    setDidId(null);
    setFormError(null);
  }, [open]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const did = choices.find((d) => d.id === didId);
    if (!did || member?.sip_endpoint_id == null) {
      setFormError("Choose a number.");
      return;
    }
    try {
      await update.mutateAsync({
        id: did.id,
        action_type: "campaign",
        campaign_id: campaign.id,
        sip_endpoint_id: member.sip_endpoint_id,
        description: did.description ?? `${name}'s number`,
        enabled: true,
      });
      toast.success("Number given", `${formatPhoneNumber(did.number)} rings ${name} first`);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not give the number.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DiscardGuard changed={didId !== null} />
        <DialogHeader>
          <DialogTitle>Give {name} a number</DialogTitle>
          <DialogDescription>
            A call to it rings {name} first, as a {campaign.name} call.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field
              label="Number"
              htmlFor="give-number"
              required
              hint="Numbers this organisation holds that don't route anywhere yet. Ask for more on Numbers."
            >
              <Select
                value={didId === null ? "" : String(didId)}
                onValueChange={(v) => setDidId(Number(v))}
              >
                <SelectTrigger id="give-number">
                  <SelectValue
                    placeholder={
                      isPending ? "Loading the numbers…" : choices.length === 0 ? "No unrouted numbers" : "Choose a number"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {choices.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)} description={d.description ?? "Not routed"}>
                      <span className="font-mono">{formatPhoneNumber(d.number)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Callout tone="info">
              When {name} can't take a call: {repNumberRule(campaign)}, the campaign's rule.
            </Callout>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={update.isPending} disabled={didId === null}>
              Give number
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function AddRepDialog({
  open, onOpenChange, campaign, members,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
  members: QueueMember[];
}) {
  const toast = useToast();
  const add = useAddLineMember();
  const [lineId, setLineId] = React.useState<number | null>(null);
  const [phone, setPhone] = React.useState<EndpointRef | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    setPhone(null);
    setLineId(campaign.queue_id ?? campaign.lines[0]?.id ?? null);
  }, [open, campaign.queue_id, campaign.lines]);

  const line = campaign.lines.find((l) => l.id === lineId);
  const changed = phone !== null || lineId !== (campaign.queue_id ?? campaign.lines[0]?.id ?? null);
  const already =
    phone !== null && members.some((m) => m.queue_id === lineId && m.sip_endpoint_id === phone.id);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!line || !phone) {
      setFormError("Choose a line and the rep's phone.");
      return;
    }
    try {
      await add.mutateAsync({ lineId: line.id, sipEndpointId: phone.id });
      toast.success("Rep added", `${phone.user_name ?? phone.extension} on ${line.name}`);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not add the rep.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>Add a rep</DialogTitle>
          <DialogDescription>
            The ACD rings this phone for the line's calls while its rep is Ready.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            {campaign.lines.length > 1 && (
              <Field label="Line" htmlFor="member-line" required hint={linePurposeText(line?.purpose ?? null).description}>
                <Select value={lineId?.toString() ?? ""} onValueChange={(v) => setLineId(Number(v))}>
                  <SelectTrigger id="member-line">
                    <SelectValue placeholder="Choose a line" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaign.lines.map((l) => (
                      <SelectItem key={l.id} value={String(l.id)} description={linePurposeText(l.purpose).label}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <Field
              label="Rep's phone"
              htmlFor="member-phone"
              required
              error={already ? "Already on this line." : undefined}
              hint="Phones somebody holds. Search by name, email or extension."
            >
              <EndpointPicker value={phone} onChange={setPhone} mode={{ kind: "assigned" }} />
            </Field>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={add.isPending} disabled={already}>
              Add rep
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
